/**
 * Lu or Labu on screen. The engine (engine.ts) decides right and wrong; this file loads the
 * picture set, turns taps and holds into answers, and keeps the clock honest.
 *
 * Inputs
 *   tap a doll          → that is your answer, committed on pointerdown's release
 *   press & hold a doll → study its face close up (costs only the clock)
 *   NO LABUBU bar       → call a round empty
 *   keys: 1–6 pick · n none · i inspect (hold) · esc pause
 *
 * Deliberately absent: any hint of how many real Labubu a round holds. Knowing the count
 * is the whole game, so the only signal that you have found them all is the round moving on.
 */
import { LuOrLabu, formatTime, type Outcome, type Tile } from './engine';
import type { GameContext } from '../registry';
import { el, toast } from '../../ui/dom';
import { showResult } from '../../ui/result';
import { luOrLabuDef, PRACTICE_ONLY } from './def';

const HOLD_MS = 380; // same threshold as tacoslap's octopus hold
const REVEAL_MS = 950;

interface ManifestImage {
  src: string;
  face: string | null;
  isLabubu: boolean;
  tier: Tile['tier'];
  brandVisible: boolean;
}

const asset = (p: string): string => `${import.meta.env.BASE_URL}${p}`.replace(/([^:])\/{2,}/g, '$1/');

export async function loadPool(signal?: AbortSignal): Promise<Tile[]> {
  const res = await fetch(asset('labubu/manifest.json'), { signal });
  if (!res.ok) throw new Error(`manifest ${res.status}`);
  const data = (await res.json()) as { images: ManifestImage[] };
  return data.images.map((i) => ({
    id: i.src,
    src: asset(i.src),
    face: i.face ? asset(i.face) : null,
    isLabubu: i.isLabubu,
    tier: i.tier,
    brandVisible: i.brandVisible,
  }));
}

function preload(urls: string[]): Promise<void> {
  return Promise.all(
    urls.map(
      (u) =>
        new Promise<void>((done) => {
          const img = new Image();
          img.onload = img.onerror = () => done();
          img.src = u;
        }),
    ),
  ).then(() => undefined);
}

export function mountLuOrLabu(ctx: GameContext): () => void {
  const { root, sfx, buzz, player } = ctx;
  let game: LuOrLabu | null = null;
  let raf = 0;
  let running = false;
  let revealing = false;
  let lastClock = '';
  const abort = new AbortController();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.delete(t);
      fn();
    }, ms);
    timers.add(t);
    return t;
  };

  // ---- DOM -----------------------------------------------------------------
  const clockEl = el('div', { class: 'll-clock' }, '0.0s');
  const roundEl = el('div', { class: 'll-round' }, '–');
  const mistakesEl = el('div', { class: 'll-mist', title: 'mistakes' }, '✕ 0');
  const windowFill = el('div', { class: 'fill' });
  const grid = el('div', { class: 'll-grid' });
  const noneBtn = el('button', { class: 'll-none', type: 'button' }, 'NO LABUBU');
  const overlay = el('div', { class: 'ts-overlay' });
  const wrap = el(
    'section',
    { class: 'll' },
    el(
      'header',
      { class: 'll-top' },
      el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'pause', onclick: () => togglePause() }, '❚❚'),
      clockEl,
      roundEl,
      mistakesEl,
    ),
    el('div', { class: 'll-window' }, windowFill),
    grid,
    noneBtn,
    overlay,
  );
  root.append(wrap);

  // one button per slot, reused every round so the browser keeps the decoded images
  const TILES = 6;
  const tileEls: HTMLButtonElement[] = [];
  const tileImgs: HTMLImageElement[] = [];
  for (let i = 0; i < TILES; i++) {
    const img = el('img', { alt: '', draggable: 'false' }) as HTMLImageElement;
    const btn = el('button', { class: 'll-tile', type: 'button', 'aria-label': `doll ${i + 1}` }, img) as HTMLButtonElement;
    bindTile(btn, i);
    tileEls.push(btn);
    tileImgs.push(img);
    grid.append(btn);
  }

  // ---- input ---------------------------------------------------------------
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let peeking = -1;

  function startPeek(i: number): void {
    const g = game;
    if (!g || !running || g.paused) return;
    const tile = g.round?.tiles[i];
    if (!tile) return;
    peeking = i;
    tileImgs[i].src = tile.face ?? tile.src;
    tileEls[i].classList.add('peek');
    sfx.tick();
  }

  function endPeek(): void {
    if (peeking < 0) return;
    const tile = game?.round?.tiles[peeking];
    if (tile) tileImgs[peeking].src = tile.src;
    tileEls[peeking].classList.remove('peek');
    peeking = -1;
  }

  function clearHold(): void {
    if (holdTimer !== null) {
      clearTimeout(holdTimer);
      timers.delete(holdTimer);
      holdTimer = null;
    }
  }

  function bindTile(btn: HTMLButtonElement, i: number): void {
    btn.addEventListener('pointerdown', (e) => {
      const g = game;
      if (!g || !running || g.paused || revealing || g.isFound(i)) return;
      e.preventDefault();
      // keeps the hold alive if the finger drifts off the tile; throws for synthetic events
      try {
        btn.setPointerCapture((e as PointerEvent).pointerId);
      } catch {
        /* no live pointer to capture */
      }
      clearHold();
      holdTimer = later(() => startPeek(i), HOLD_MS);
    });
    btn.addEventListener('pointerup', (e) => {
      e.preventDefault();
      const wasPeeking = peeking === i;
      clearHold();
      endPeek();
      // a hold is for looking, not for choosing
      if (wasPeeking) return;
      answer(() => game?.pick(i, performance.now()) ?? null);
    });
    btn.addEventListener('pointercancel', () => {
      clearHold();
      endPeek();
    });
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  noneBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (revealing) return;
    answer(() => game?.declareNone(performance.now()) ?? null);
  });

  function onKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    if (e.key === 'Escape') return togglePause();
    if (!running || revealing) return;
    if (e.key === 'n') return void answer(() => game?.declareNone(performance.now()) ?? null);
    if (e.key === 'i') {
      const g = game;
      if (!g?.round) return;
      g.round.tiles.forEach((t, i) => {
        tileImgs[i].src = t.face ?? t.src;
        tileEls[i].classList.add('peek');
      });
      return;
    }
    const n = Number(e.key);
    if (n >= 1 && n <= TILES) answer(() => game?.pick(n - 1, performance.now()) ?? null);
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (e.key !== 'i') return;
    const g = game;
    if (!g?.round) return;
    g.round.tiles.forEach((t, i) => {
      tileImgs[i].src = t.src;
      tileEls[i].classList.remove('peek');
    });
  }
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKeyUp);

  // ---- rounds --------------------------------------------------------------
  function renderRound(): void {
    const g = game;
    const r = g?.round;
    if (!g || !r) return;
    roundEl.textContent = `${g.roundIndex + 1}/${g.rules.rounds}`;
    r.tiles.forEach((t, i) => {
      tileImgs[i].src = t.src;
      tileEls[i].className = 'll-tile';
      tileEls[i].disabled = false;
    });
    grid.classList.remove('in');
    void grid.offsetWidth;
    grid.classList.add('in');
  }

  function answer(act: () => Outcome | null): void {
    const g = game;
    if (!g || !running || g.paused || revealing) return;
    const out = act();
    if (!out) return;
    mistakesEl.textContent = `✕ ${g.mistakes}`;

    if (out.ok && !out.roundOver) {
      // one down, but we never say how many are left — that is the game
      tileEls[out.index!].classList.add('found');
      tileEls[out.index!].disabled = true;
      sfx.tick();
      buzz.light();
      return;
    }

    if (out.ok) {
      sfx.slap();
      buzz.success();
    } else {
      sfx.buzz();
      buzz.error();
    }
    reveal(out);
  }

  /** freeze the clock, show what the round actually held, then deal the next one */
  function reveal(out: Outcome): void {
    const g = game!;
    const now = performance.now();
    revealing = true;
    g.pause(now);

    for (const i of out.targets) tileEls[i].classList.add('was-real');
    if (!out.ok && out.did === 'pick' && out.index !== undefined) tileEls[out.index].classList.add('wrong');
    for (const b of tileEls) b.disabled = true;

    if (!out.ok) {
      const n = out.targets.length;
      if (out.did === 'timeout') toast(wrap, 'Too slow!', 'bad');
      else if (out.did === 'none') toast(wrap, n === 1 ? 'There was one!' : `There were ${n}!`, 'bad');
      else toast(wrap, n === 0 ? 'That was a lafufu — this round had none' : 'That was a lafufu — check the teeth', 'bad');
    }

    if (out.won) {
      later(() => void finish(), REVEAL_MS);
      return;
    }
    later(() => {
      revealing = false;
      g.resume(performance.now());
      renderRound();
    }, REVEAL_MS);
  }

  // ---- loop ----------------------------------------------------------------
  function frame(now: number): void {
    if (!running) return;
    const g = game!;
    const out = revealing ? null : g.tick(now);
    if (out) {
      mistakesEl.textContent = `✕ ${g.mistakes}`;
      sfx.buzz();
      buzz.error();
      reveal(out);
    }
    const clock = formatTime(g.elapsed(now));
    if (clock !== lastClock) {
      clockEl.textContent = clock;
      lastClock = clock;
    }
    const r = g.remaining(now);
    windowFill.style.transform = `scaleX(${r.toFixed(3)})`;
    windowFill.classList.toggle('low', r < 0.3);
    raf = requestAnimationFrame(frame);
  }

  // ---- start / pause / finish ----------------------------------------------
  function sheet(...kids: (Node | string)[]): void {
    overlay.replaceChildren(el('div', { class: 'sheet' }, ...kids));
    overlay.classList.add('show');
  }

  async function ready(): Promise<void> {
    sheet(el('h2', {}, 'Lu or Labu'), el('p', { class: 'dim' }, 'Loading the dolls…'));
    let pool: Tile[];
    try {
      pool = await loadPool(abort.signal);
    } catch (err) {
      if (abort.signal.aborted) return;
      sheet(
        el('h2', {}, 'Could not load the dolls'),
        el('p', { class: 'dim' }, 'The picture set did not download. Check your connection and try again.'),
        el('button', { class: 'btn primary big', type: 'button', onclick: () => void ready() }, 'Retry'),
        el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Menu'),
      );
      return;
    }
    game = new LuOrLabu(pool, { tiles: TILES });
    const rounds = game.prepare();
    // the first round must not measure the network
    const urls = new Set<string>();
    for (const r of rounds) for (const t of r.tiles) (urls.add(t.src), t.face && urls.add(t.face));
    await preload([...urls]);
    if (abort.signal.aborted) return;

    sheet(
      el('h2', {}, 'Lu or Labu'),
      el('p', {}, 'Tap the real Labubu. Some rounds hide two. Some hide none.'),
      el('p', { class: 'dim' }, 'Press and hold a doll to study its face.'),
      el('button', { class: 'btn primary big', type: 'button', onclick: () => countdown() }, 'Start'),
    );
  }

  function countdown(): void {
    sfx.unlock();
    let n = 3;
    const big = el('div', { class: 'count' }, String(n));
    overlay.replaceChildren(big);
    const step = () => {
      n--;
      if (n <= 0) {
        overlay.classList.remove('show');
        overlay.replaceChildren();
        start();
        return;
      }
      big.textContent = String(n);
      big.classList.remove('pop');
      void big.offsetWidth;
      big.classList.add('pop');
      sfx.tick();
      later(step, 600);
    };
    sfx.tick();
    later(step, 600);
  }

  function start(): void {
    const g = game!;
    g.start(performance.now());
    running = true;
    revealing = false;
    lastClock = '';
    mistakesEl.textContent = '✕ 0';
    renderRound();
    raf = requestAnimationFrame(frame);
  }

  function togglePause(): void {
    const g = game;
    if (!g || !running) return;
    if (g.paused) {
      g.resume(performance.now());
      overlay.classList.remove('show');
      overlay.replaceChildren();
      raf = requestAnimationFrame(frame);
    } else {
      g.pause(performance.now());
      cancelAnimationFrame(raf);
      clearHold();
      endPeek();
      sheet(
        el('h2', {}, 'Paused'),
        el('button', { class: 'btn primary big', type: 'button', onclick: () => togglePause() }, 'Resume'),
        el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Menu'),
      );
    }
  }

  function restart(): void {
    running = false;
    revealing = false;
    cancelAnimationFrame(raf);
    clearHold();
    endPeek();
    overlay.classList.remove('show');
    overlay.replaceChildren();
    game!.prepare();
    countdown();
  }

  async function finish(): Promise<void> {
    const g = game!;
    running = false;
    revealing = false;
    cancelAnimationFrame(raf);
    const score = Math.round(g.elapsed(performance.now()));
    clockEl.textContent = formatTime(score);
    windowFill.style.transform = 'scaleX(0)';
    sfx.win();
    buzz.success();
    const result = {
      score,
      mistakes: g.mistakes,
      detail: { bestStreak: g.bestStreak, rounds: g.rules.rounds, rules: g.rules },
      practice: PRACTICE_ONLY || player.practice,
    };
    const { board, newBest } = await ctx.finish(result);
    showResult(overlay, {
      game: luOrLabuDef,
      result,
      board,
      newBest,
      player,
      post: () => ctx.post(result),
      again: () => restart(),
      board_: () => ctx.showBoard(),
      menu: () => ctx.exit(),
    });
  }

  function onHidden(): void {
    if (document.hidden && running && !game?.paused) togglePause();
  }
  document.addEventListener('visibilitychange', onHidden);

  ctx.setBackHandler(() => {
    if (running && game && !game.paused) {
      togglePause();
      return true;
    }
    return false;
  });

  void ready();

  return () => {
    running = false;
    abort.abort();
    cancelAnimationFrame(raf);
    clearHold();
    endPeek();
    for (const t of timers) clearTimeout(t);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('keyup', onKeyUp);
    document.removeEventListener('visibilitychange', onHidden);
    ctx.setBackHandler(null);
    wrap.remove();
  };
}
